import OpacityOutlined from '@mui/icons-material/OpacityOutlined'
import { Box, Popover, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material'
import { differenceInDays } from 'date-fns'
import { useMemo, useRef, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import GenericInformationTooltip from 'src/components/common/GenericInformationTootip'
import { IMainComponentStatus } from 'src/generated-types/main-component-status'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { formatDaysToText, getEquipmentTooltipData, getFormattedDate, GetRGBA, getThemeColor } from 'src/utils'
import { ReactComponent as CustomEquipmentType } from '../../../../assets/icons/CustomEquipmentType.svg'
import { ReactComponent as Ring } from '../../../../assets/svg/equipments/ring.svg'
import { ReactComponent as LiceSkirt } from '../../../../assets/svg/LiceSkirt.svg'
import { ReactComponent as Net } from '../../../../assets/svg/net.svg'
import { DynamicTextTypography, IDynamicTextTypographyProps } from '../../../../components/DynamicTextTypography'
import { CustomerLocationCtx } from '../../../../context/FacilityContext'
import {
  CertificationValidationPeriod,
  DialogContextType,
  IMainComponentType,
  MainComponentStatus,
} from '../../../../enums'
import { IMainComponent } from '../../../../generated-types/main-component'
import { useDialogCtx } from '../../../../hooks/context-hooks'
import { MainComponentProgress } from '../../locality-dashboard/cage-status/MainComponentProgress'
import { EquipmentDetailModal } from '../../mainComponents/EquipmentDetailModal'

export const StyledImage = styled('img')({
  height: 14,
  width: 14,
})

export const getMainComponentIcon = (
  type: IMainComponentType,
  t: TFunction<'cage-status', undefined>,
  color: string,
) => {
  switch (type) {
    case IMainComponentType.Ring:
      return (
        <Tooltip title={<Typography>{t('ring')}</Typography>}>
          <Ring height={'28px'} width={'28px'} color={color} />
        </Tooltip>
      )
    case IMainComponentType.Net:
      return (
        <Tooltip title={<Typography>{t('net')}</Typography>}>
          <Net height={'28px'} width={'28px'} />
        </Tooltip>
      )
    case IMainComponentType.LiceSkirt:
      return (
        <Tooltip title={<Typography>{t('liceskirt')}</Typography>}>
          <LiceSkirt height={'28px'} width={'28px'} />
        </Tooltip>
      )
    default:
      return (
        <Tooltip title={<Typography>{t('custom')}</Typography>}>
          <CustomEquipmentType height={'22px'} width={'22px'} />
        </Tooltip>
      )
  }
}

export const MainComponentItem: React.FC<{
  mainComponent: IMainComponent
  type: IMainComponentType
  textColor?: any
  removePopOver?: boolean
  itemTextProps?: Omit<IDynamicTextTypographyProps, 'text'>
  permission?: number
  openEquipmentModal?: boolean
  fontSize?: any
  fontweight?: any
  isTooltipDisabled?: boolean
  showExtraDetails?: boolean
  showLastWashed?: boolean
}> = ({
  mainComponent,
  type,
  textColor,
  removePopOver = false,
  itemTextProps,
  permission,
  openEquipmentModal = true,
  fontSize,
  fontweight,
  isTooltipDisabled = false,
  showExtraDetails = false,
  showLastWashed = false,
}) => {
  const { localityId, customerId } = useParams()
  const { showDialog } = useDialogCtx()
  const theme = useTheme()
  const { t } = useTranslation('cage-status')

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isPopoverHovered, setIsPopoverHovered] = useState(false)
  const closeTimeoutRef = useRef<number | null>(null)

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const handleTargetMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    closeTimeoutRef.current = window.setTimeout(() => {
      if (!isPopoverHovered) {
        handlePopoverClose()
      }
    }, 100)
  }

  const open = Boolean(anchorEl)

  const icons = useMemo(() => {
    if (type === IMainComponentType.Ring) {
      return getMainComponentIcon(type, t, GetRGBA(getThemeColor(theme, MUIThemeColors.primaryMain), 0.5))
    }
    return getMainComponentIcon(type, t, getThemeColor(theme, MUIThemeColors.primaryMain))
  }, [type, t])

  const certificate = useMemo(() => {
    const validCertificateList =
      mainComponent?.certificates &&
      (mainComponent?.certificates ?? [])
        .filter(c => c.isValid)
        .sort((a, b) => new Date(b.validUntil).getTime() - new Date(a.validUntil).getTime())
    if (validCertificateList && validCertificateList.length > 0) return validCertificateList[0]
  }, [mainComponent?.certificates])

  const certificateValidationTime = useMemo(() => {
    if (certificate?.expiresIn == null || certificate?.numberOfDays == null) return null
    if (mainComponent.status === MainComponentStatus.Active && mainComponent.expiryDate && certificate?.numberOfDays) {
      const expiryDate = mainComponent.expiryDate
      const daysRemaining = differenceInDays(new Date(expiryDate), new Date())
      return (daysRemaining * 100) / certificate?.numberOfDays
    }
    if (certificate?.expiresIn && certificate?.numberOfDays) {
      return (certificate?.expiresIn * 100) / certificate?.numberOfDays
    }
    return 0
  }, [certificate])

  const status: CertificationValidationPeriod = useMemo(() => {
    if (certificateValidationTime != undefined && certificateValidationTime >= 0) {
      if (certificateValidationTime < CertificationValidationPeriod.LOW) {
        return CertificationValidationPeriod.LOW
      } else if (certificateValidationTime < CertificationValidationPeriod.MEDIUM) {
        return CertificationValidationPeriod.MEDIUM
      } else if (certificateValidationTime <= CertificationValidationPeriod.HIGH) {
        return CertificationValidationPeriod.HIGH
      }
    }
    return CertificationValidationPeriod.NA
  }, [certificateValidationTime])

  const lastWashedTooltip = useMemo(() => {
    const label = t('lastWashed', { ns: 'common' })
    if (!mainComponent?.lastWashedDate) {
      return (
        <Box sx={{ py: 0.25 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, opacity: 0.85 }}>{label}</Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{t('never', { ns: 'common' })}</Typography>
        </Box>
      )
    }
    const washedOn = new Date(mainComponent.lastWashedDate)
    const relative = formatDaysToText({ startDate: washedOn, endDate: new Date() }).trim()
    return (
      <Box sx={{ py: 0.25 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 600, opacity: 0.85 }}>{label}</Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {getFormattedDate(washedOn)}
        </Typography>
        {relative && (
          <Typography sx={{ fontSize: '11px', opacity: 0.85 }}>
            {`${relative} ${t('ago', { ns: 'common' })}`}
          </Typography>
        )}
      </Box>
    )
  }, [mainComponent?.lastWashedDate, t])

  const onClickMainComponent = () => {
    handlePopoverClose()
    showDialog(
      EquipmentDetailModal,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          permission: permission,
          context: CustomerLocationCtx,
          mainComponentId: mainComponent.id,
          isWithLayout: true,
          shouldCloseMainDialog: true,
          mainComponentTypeId: mainComponent.type.id,
        },
        opts: {
          maxWidth: false,
          fullWidth: true,
          bgColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
          rootWidth: '1400px',
        },
      },
      DialogContextType.localityContext,
      true,
    )
  }

  return (
    <Flex.Row alignItems={'center'} gap={1}>
      <Stack
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'center'}
        gap={1}
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handleTargetMouseLeave}
        onClick={openEquipmentModal ? onClickMainComponent : undefined}
        className="hover-pointer"
      >
        {icons}
        <GenericInformationTooltip
          data={getEquipmentTooltipData(mainComponent)}
          isTooltipDisabled={!removePopOver || !showExtraDetails}
        >
          <DynamicTextTypography
            color={textColor ? textColor : theme.palette.black[theme.palette.mode]}
            variant="body1"
            fontWeight={fontweight ?? '500'}
            fontSize={fontSize}
            key={`${mainComponent.id}`}
            text={mainComponent?.serialNumber ?? '-'}
            maxLength={10}
            isTooltipDisabled={isTooltipDisabled}
            {...itemTextProps}
          />
        </GenericInformationTooltip>
        {!removePopOver && (
          <Popover
            id="mouse-over-popover"
            sx={{
              pointerEvents: 'none',
            }}
            PaperProps={{
              sx: { pointerEvents: 'auto' },
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Flex.Column
              px={2}
              py={1}
              spacing={1}
              onMouseEnter={() => {
                setIsPopoverHovered(true)
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current)
                  closeTimeoutRef.current = null
                }
              }}
              onMouseLeave={() => {
                setIsPopoverHovered(false)
                handlePopoverClose()
              }}
            >
              <Flex.Row justifyContent={'flex-start'} alignItems={'center'} gap={1}>
                {icons}
                <DynamicTextTypography
                  color={textColor ? textColor : theme.palette.black[theme.palette.mode]}
                  variant="body2"
                  fontWeight={'500'}
                  fontSize={fontSize}
                  key={`${mainComponent.id}`}
                  text={mainComponent?.serialNumber}
                  maxLength={50}
                />
              </Flex.Row>
              <MainComponentProgress
                minWidth={'200px'}
                cage={
                  {
                    nets: type === IMainComponentType.Net ? [mainComponent] : [],
                    rings: type === IMainComponentType.Ring ? [mainComponent] : [],
                    daysLeft: mainComponent.expires,
                    certificateValidationTime: certificateValidationTime,
                  } as IMainComponentStatus
                }
                status={status}
                permission={permission}
              />
              {showExtraDetails &&
                getEquipmentTooltipData(mainComponent)
                  .filter(r => r.key != 'serialNumber')
                  .map(record => (
                    <Box key={record.key} sx={{ fontSize: '12px', display: 'flex', flexDirection: 'row', gap: '4px' }}>
                      <strong>{record.name} : </strong>{' '}
                      <DynamicTextTypography text={record.value.toString()} maxLength={45} />
                    </Box>
                  ))}
              {showLastWashed && type === IMainComponentType.Net && (
                <Box sx={{ fontSize: '12px', display: 'flex', flexDirection: 'row', gap: '4px' }}>
                  <strong>{t('lastWashed', { ns: 'common' })} : </strong>
                  <span>
                    {mainComponent?.lastWashedDate ? getFormattedDate(new Date(mainComponent.lastWashedDate)) : '—'}
                  </span>
                </Box>
              )}
            </Flex.Column>
          </Popover>
        )}
      </Stack>
      {showLastWashed && type === IMainComponentType.Net && (
        // Sibling of the popover-trigger stack, so hovering last-washed shows only this tooltip and
        // never opens the equipment popover.
        <Tooltip arrow placement="top" title={lastWashedTooltip}>
          <Flex.Row alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
            <OpacityOutlined
              aria-label={(t('lastWashed', { ns: 'common' }) as string) ?? ''}
              sx={{
                fontSize: '14px',
                color: getThemeColor(theme, MUIThemeColors.secondaryMain),
                // Muted when washed; fainter when never washed. Neutral by design - colour-coding
                // "overdue" would need a configured wash interval, which doesn't exist yet.
                opacity: mainComponent?.lastWashedDate ? 0.85 : 0.35,
              }}
            />
            {mainComponent?.lastWashedDate && (
              // Date inline only when there's room (lg+); narrower screens get the drop alone, with
              // the date still available via the tooltip.
              <Typography
                sx={{
                  display: { xs: 'none', lg: 'inline' },
                  fontSize: '12px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
                }}
              >
                {getFormattedDate(new Date(mainComponent.lastWashedDate))}
              </Typography>
            )}
          </Flex.Row>
        </Tooltip>
      )}
    </Flex.Row>
  )
}
